import { Inbox } from 'lucide-react'
import { Button, Badge, Card, Select } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import type { SelectOption } from '@/components/ui'
import { formatCurrency, formatDate } from '@/lib/formatters'
import {
  penaltyStatusBadgeVariant,
  penaltyStatusLabel,
  resolveEmployeeName,
  resolvePenaltyTypeName,
} from './penaltyUtils'
import type { PenaltyEntry, PenaltyEntryStatus, PenaltyType } from '@/features/penalties/types/penalty.types'
import { QueryErrorState } from '@/components/common/QueryErrorState'

const STATUS_OPTIONS: SelectOption[] = [
  { value: '', label: 'All statuses' },
  { value: 'pending_approval', label: 'Pending approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'reversed', label: 'Reversed' },
]

interface PenaltyEntryTableProps {
  entries: PenaltyEntry[]
  types: PenaltyType[]
  isLoading: boolean
  error: Error | null
  statusFilter: string
  onStatusFilterChange: (status: string) => void
  onRowClick: (entryId: string) => void
  onRetry: () => void
  showEmployee?: boolean
  showCompany?: boolean
  companyNameById?: Map<string, string>
  emptyMessage?: string
}

export function PenaltyEntryTable({
  entries,
  types,
  isLoading,
  error,
  statusFilter,
  onStatusFilterChange,
  onRowClick,
  onRetry,
  showEmployee = false,
  showCompany = false,
  companyNameById,
  emptyMessage = 'No penalty records found.',
}: PenaltyEntryTableProps): React.ReactElement {
  return (
    <Card
      contentClassName="p-0"
      header={
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status</span>
          <div className="w-[200px]">
            <Select
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={onStatusFilterChange}
            />
          </div>
        </label>
      }
    >
      {isLoading ? (
        <SectionLoader variant="table" cols={7} rows={6} label="Loading penalties" />
      ) : error ? (
        <QueryErrorState
          error={error}
          title="Couldn't load penalties"
          onRetry={onRetry}
          className="border-0 bg-transparent dark:border-0 dark:bg-transparent"
        />
      ) : entries.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <Inbox className="h-12 w-12 text-gray-400 dark:text-gray-500" aria-hidden />
          <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">No penalties</h3>
          <p className="max-w-sm text-sm text-gray-600 dark:text-gray-400">{emptyMessage}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table
            className="w-full min-w-[600px] border-collapse text-sm"
            aria-label="Penalty entries"
          >
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
                {showCompany && (
                  <th
                    scope="col"
                    className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300"
                  >
                    Company
                  </th>
                )}
                <th
                  scope="col"
                  className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300"
                >
                  Date
                </th>
                {showEmployee && (
                  <th
                    scope="col"
                    className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300"
                  >
                    Employee
                  </th>
                )}
                <th
                  scope="col"
                  className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300"
                >
                  Type
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-right font-medium text-gray-700 dark:text-gray-300"
                >
                  Amount
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300"
                >
                  Status
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-right font-medium text-gray-700 dark:text-gray-300"
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr
                  key={entry._id}
                  className="border-b border-gray-200 dark:border-gray-700 last:border-0"
                >
                  {showCompany && (
                    <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                      {companyNameById?.get(entry.companyId) ?? '—'}
                    </td>
                  )}
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                    {formatDate(entry.penaltyDate)}
                  </td>
                  {showEmployee && (
                    <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                      {resolveEmployeeName(entry.employeeName, entry.employeeId, entry.userId)}
                    </td>
                  )}
                  <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                    {resolvePenaltyTypeName(
                      entry.penaltyTypeId,
                      entry.customTypeName,
                      types,
                      entry.penaltyTypeName,
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-gray-100">
                    {formatCurrency(entry.amount, entry.currency)}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={penaltyStatusBadgeVariant(entry.status as PenaltyEntryStatus)}>
                      {penaltyStatusLabel(entry.status as PenaltyEntryStatus)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-8 text-xs"
                      onClick={() => onRowClick(entry._id)}
                      aria-label="View penalty"
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

PenaltyEntryTable.displayName = 'PenaltyEntryTable'
